import React, { useState, useEffect, useContext } from "react";
import { API, AuthContext } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { CreditCard, RefreshCw, CheckCircle2, Flag } from "lucide-react";
import { toast } from "sonner";
import VisitorTraffic from "./VisitorTraffic";

export default function SubscriptionsAdmin() {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState("subscriptions");
  const [subs, setSubs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [newTier, setNewTier] = useState({ name: "", interval: "monthly", amount: "" });
  const [migrate, setMigrate] = useState({ from_tier_id: "", to_tier_id: "", effective_in_days: 30 });

  const h = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const [s, t, m] = await Promise.all([
      fetch(`${API}/admin/subscriptions`, { headers: h() }),
      fetch(`${API}/admin/price-tiers`, { headers: h() }),
      fetch(`${API}/admin/subscription-metrics`, { headers: h() }),
    ]);
    if (s.ok) setSubs(await s.json());
    if (t.ok) setTiers(await t.json());
    if (m.ok) setMetrics(await m.json());
  };
  useEffect(() => { if (isAdmin) load(); }, []); // eslint-disable-line

  const confirm = async (id) => {
    const r = await fetch(`${API}/admin/subscriptions/${id}/confirm`, { method: "POST", headers: h() });
    if (r.ok) { toast.success("Confirmed"); load(); }
  };
  const flag = async (id) => {
    const r = await fetch(`${API}/admin/subscriptions/${id}/flag`, { method: "POST", headers: h() });
    if (r.ok) { toast.success("Flag toggled"); load(); }
  };
  const createTier = async () => {
    if (!newTier.name || !newTier.amount) return toast.error("Name and amount required");
    const r = await fetch(`${API}/admin/price-tiers`, {
      method: "POST", headers: { ...h(), "Content-Type": "application/json" },
      body: JSON.stringify({ ...newTier, amount: parseFloat(newTier.amount), set_current: true }),
    });
    if (r.ok) { toast.success("Price tier created & set current"); setNewTier({ name: "", interval: "monthly", amount: "" }); load(); }
    else toast.error("Could not create tier");
  };
  const setCurrent = async (id) => {
    const r = await fetch(`${API}/admin/price-tiers/${id}/set-current`, { method: "POST", headers: h() });
    if (r.ok) { toast.success("Set as current"); load(); }
  };
  const runMigrate = async () => {
    if (!migrate.from_tier_id || !migrate.to_tier_id) return toast.error("Select both tiers");
    const r = await fetch(`${API}/admin/subscriptions/migrate`, {
      method: "POST", headers: { ...h(), "Content-Type": "application/json" },
      body: JSON.stringify({ ...migrate, effective_in_days: parseInt(migrate.effective_in_days, 10) }),
    });
    const d = await r.json();
    if (r.ok) toast.success(`Notified ${d.notified} subscriber(s), effective ${new Date(d.effective).toLocaleDateString("en-AU")}`);
    else toast.error(d.detail || "Migration failed");
  };

  if (!isAdmin) return <div className="p-8 text-center text-slate-500">Admin access only.</div>;

  const Stat = ({ label, value, testid }) => (
    <Card><CardContent className="py-4"><p className="text-2xl font-bold text-slate-900" data-testid={testid}>{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="subscriptions-admin-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center"><CreditCard className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Subscriptions</h1>
            <p className="text-sm text-slate-500">Revenue, subscribers, price tiers & migrations</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="subs-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="MRR (AUD)" value={`$${metrics.mrr}`} testid="metric-mrr" />
          <Stat label="ARR (AUD)" value={`$${metrics.arr}`} testid="metric-arr" />
          <Stat label="Active" value={metrics.active} testid="metric-active-subs" />
          <Stat label="Cancelling" value={metrics.cancelling} testid="metric-cancelling" />
          <Stat label="Flagged" value={metrics.flagged} testid="metric-flagged" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="subscriptions" data-testid="subs-tab-list">Subscriptions</TabsTrigger>
          <TabsTrigger value="tiers" data-testid="subs-tab-tiers">Price Tiers</TabsTrigger>
          <TabsTrigger value="migrate" data-testid="subs-tab-migrate">Notify & Migrate</TabsTrigger>
          <TabsTrigger value="visitors" data-testid="subs-tab-visitors">Visitor Traffic</TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions" className="mt-4">
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr><th className="p-3">Subscriber</th><th className="p-3">Plan</th><th className="p-3">Status</th><th className="p-3">Renews/Ends</th><th className="p-3">Confirmed</th><th className="p-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {subs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No subscriptions yet.</td></tr>}
                {subs.map((s) => (
                  <tr key={s.id} className="border-t" data-testid={`sub-row-${s.id}`}>
                    <td className="p-3"><div>{s.user_name}</div><div className="text-xs text-slate-400">{s.user_email}</div></td>
                    <td className="p-3 capitalize">{s.billing_interval} · ${s.amount}</td>
                    <td className="p-3">
                      {s.flagged && <Badge className="bg-orange-100 text-orange-700 border-0 mr-1">Flagged</Badge>}
                      {s.cancel_at_period_end ? <Badge className="bg-rose-100 text-rose-700 border-0">Cancelling</Badge>
                        : <Badge className="bg-green-100 text-green-700 border-0">{s.status}</Badge>}
                    </td>
                    <td className="p-3 whitespace-nowrap">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("en-AU") : "—"}</td>
                    <td className="p-3">{s.admin_confirmed ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <span className="text-slate-400">—</span>}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        {!s.admin_confirmed && <Button size="sm" variant="outline" className="h-8" onClick={() => confirm(s.id)} data-testid={`sub-confirm-${s.id}`}>Confirm</Button>}
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => flag(s.id)} data-testid={`sub-flag-${s.id}`}><Flag className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tiers" className="mt-4 space-y-4">
          <Card><CardContent className="py-4">
            <p className="font-semibold text-slate-800 mb-3">Create new price tier (existing subscribers are grandfathered)</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div><Label>Name</Label><Input value={newTier.name} onChange={(e) => setNewTier((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Monthly 2027" data-testid="tier-name" /></div>
              <div><Label>Interval</Label>
                <Select value={newTier.interval} onValueChange={(v) => setNewTier((p) => ({ ...p, interval: v }))}>
                  <SelectTrigger data-testid="tier-interval"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Amount (AUD, GST inc)</Label><Input type="number" value={newTier.amount} onChange={(e) => setNewTier((p) => ({ ...p, amount: e.target.value }))} placeholder="79" data-testid="tier-amount" /></div>
              <Button onClick={createTier} className="bg-amber-600 hover:bg-amber-700" data-testid="tier-create-btn">Create & Set Current</Button>
            </div>
            {(() => {
              const m = tiers.find((t) => t.interval === "monthly" && t.is_current);
              const annual2mf = m ? Math.round(m.amount * 10) : null;
              return (
                <p className="text-xs text-slate-500 mt-3" data-testid="annual-helper">
                  💡 <b>Annual "2 months free"</b> = pay for 10 months = <b>10 × monthly</b>.
                  {m ? <> Current monthly is <b>${m.amount}</b>, so a 2-months-free annual = <b>${annual2mf}</b>.</> : " Set a current monthly tier first."}
                  {m && newTier.interval === "annual" && (
                    <button type="button" onClick={() => setNewTier((p) => ({ ...p, amount: String(annual2mf), name: p.name || "Annual (2 months free)" }))}
                      className="ml-2 underline text-amber-700 hover:text-amber-800" data-testid="use-2-months-free-btn">
                      Use ${annual2mf}
                    </button>
                  )}
                </p>
              );
            })()}
          </CardContent></Card>
          <Card><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3">Name</th><th className="p-3">Interval</th><th className="p-3">Amount</th><th className="p-3">Current</th><th className="p-3">Subscribers</th><th className="p-3 text-right">Actions</th></tr></thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.id} className="border-t" data-testid={`tier-row-${t.id}`}>
                    <td className="p-3">{t.name}</td><td className="p-3 capitalize">{t.interval}</td>
                    <td className="p-3">${t.amount} {t.currency?.toUpperCase()}</td>
                    <td className="p-3">{t.is_current ? <Badge className="bg-green-100 text-green-700 border-0">Current</Badge> : "—"}</td>
                    <td className="p-3">{t.active_subscribers}</td>
                    <td className="p-3 text-right">{!t.is_current && <Button size="sm" variant="outline" className="h-8" onClick={() => setCurrent(t.id)} data-testid={`tier-setcurrent-${t.id}`}>Set Current</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="migrate" className="mt-4">
          <Card><CardContent className="py-5 space-y-4 max-w-xl">
            <p className="text-sm text-slate-600">Move subscribers from one price tier to another. Minimum 30 days notice is enforced; all affected subscribers are notified.</p>
            <div><Label>From tier</Label>
              <Select value={migrate.from_tier_id} onValueChange={(v) => setMigrate((p) => ({ ...p, from_tier_id: v }))}>
                <SelectTrigger data-testid="migrate-from"><SelectValue placeholder="Select tier" /></SelectTrigger>
                <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (${t.amount})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>To tier</Label>
              <Select value={migrate.to_tier_id} onValueChange={(v) => setMigrate((p) => ({ ...p, to_tier_id: v }))}>
                <SelectTrigger data-testid="migrate-to"><SelectValue placeholder="Select tier" /></SelectTrigger>
                <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (${t.amount})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Effective in (days, min 30)</Label><Input type="number" value={migrate.effective_in_days} onChange={(e) => setMigrate((p) => ({ ...p, effective_in_days: e.target.value }))} data-testid="migrate-days" /></div>
            <Button onClick={runMigrate} className="bg-amber-600 hover:bg-amber-700" data-testid="migrate-run-btn">Notify & Migrate</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="visitors" className="mt-4">
          <VisitorTraffic />
        </TabsContent>
      </Tabs>
    </div>
  );
}
